// src/components/sessions/SessionList.jsx
import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock, 
  Users, 
  BookOpen,
  School,
  Briefcase,
  Target,
  BarChart3,
  Eye,
  Edit,
  Trash2,
  Plus,
  Search,
  Filter,
  Grid3X3,
  List,
  CheckCircle,
  AlertCircle,
  TrendingUp
} from 'lucide-react';
import { SessionService } from '../../services/sessionService';
import { AttendanceService } from '../../services/attendanceService';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const SessionList = ({onCreateSession, onSessionSelect, onEditSession, onRecordAttendance}) => {
  const { userProfile, userContexts } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('cards');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    context: '',
    subject: '',
    grade: '',
    sessionType: '',
    dateRange: ''
  });

  useEffect(() => {
    loadSessions();
    loadAttendanceRecords();
  }, []);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const data = await SessionService.getUserSessions();
      setSessions(data);
    } catch (err) {
      setError('Failed to load sessions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAttendanceRecords = async () => {
    try {
      const records = await AttendanceService.getAttendanceRecords();
      setAttendanceRecords(records);
    } catch (err) {
      console.error('Failed to load attendance records:', err);
    }
  };

  // Get attendance stats for a session
  const getSessionAttendanceStats = (sessionId) => {
    const sessionAttendance = attendanceRecords.filter(record => record.sessionId === sessionId);
    
    if (sessionAttendance.length === 0) {
      return { recordCount: 0, averageAttendanceRate: 0, lastRecorded: null };
    }

    const averageRate = sessionAttendance.reduce((sum, record) => 
      sum + parseFloat(record.stats?.attendanceRate || 0), 0) / sessionAttendance.length;

    const lastRecord = sessionAttendance.sort((a, b) => 
      new Date(b.recordedAt) - new Date(a.recordedAt))[0];

    return {
      recordCount: sessionAttendance.length,
      averageAttendanceRate: averageRate.toFixed(1),
      lastRecorded: lastRecord.recordedAt
    };
  };

  // Filter sessions
  const filteredSessions = sessions.filter(session => {
    const matchesSearch = searchTerm === '' || 
      session.sessionTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.subject.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesContext = filters.context === '' || session.contextId === filters.context;
    const matchesSubject = filters.subject === '' || session.subject === filters.subject;
    const matchesGrade = filters.grade === '' || session.grades.includes(filters.grade);
    const matchesType = filters.sessionType === '' || session.sessionType === filters.sessionType;

    return matchesSearch && matchesContext && matchesSubject && matchesGrade && matchesType;
  });

  const handleCreateSession = () => {
    onCreateSession();  // Use prop function instead of navigate
  };

  const handleViewSession = (session) => {
    onSessionSelect(session);  
  };

  const handleEditSession = (session) => {
    onEditSession(session);  
  };

  const handleRecordAttendance = (sessionId) => {
   onRecordAttendance(sessionId);
  };

  const handleDeleteSession = async (sessionId, sessionTitle) => {
    if (window.confirm(`Are you sure you want to delete "${sessionTitle}"?`)) {
      try {
        await SessionService.deleteSession(sessionId);
        await loadSessions();
      } catch (err) {
        setError('Failed to delete session');
      }
    }
  };

  const getSessionTypeIcon = (sessionContext) => {
    if (sessionContext?.type === 'institution') {
      return <School className="w-4 h-4 text-blue-500" />;
    } else {
      return <Briefcase className="w-4 h-4 text-green-500" />;
    }
  };

  const getSessionCategoryColor = (category) => {
    switch (category) {
      case 'Lesson':
        return 'bg-blue-100 text-blue-800';
      case 'Assessment':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get unique values for filters
  const uniqueContexts = [...new Set(sessions.map(s => s.contextId))];
  const uniqueSubjects = [...new Set(sessions.map(s => s.subject))];
  const uniqueGrades = [...new Set(sessions.flatMap(s => s.grades))];
  const uniqueTypes = [...new Set(sessions.map(s => s.sessionType))];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading sessions...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sessions</h1>
            <p className="text-gray-600">Manage your lessons and assessments</p>
          </div>
          <button
            onClick={handleCreateSession}
            className="mt-4 md:mt-0 flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Session
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search sessions..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Context Filter */}
            <div>
              <select
                value={filters.context}
                onChange={(e) => setFilters(prev => ({ ...prev, context: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Contexts</option>
                {uniqueContexts.map(contextId => {
                  const context = userContexts?.find(ctx => ctx.id === contextId);
                  return context ? (
                    <option key={contextId} value={contextId}>{context.name}</option>
                  ) : null;
                })}
              </select>
            </div>

            {/* Subject Filter */}
            <div>
              <select
                value={filters.subject}
                onChange={(e) => setFilters(prev => ({ ...prev, subject: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Subjects</option>
                {uniqueSubjects.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>

            {/* Grade Filter */}
            <div>
              <select
                value={filters.grade}
                onChange={(e) => setFilters(prev => ({ ...prev, grade: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Grades</option>
                {uniqueGrades.map(grade => (
                  <option key={grade} value={grade}>Grade {grade}</option>
                ))}
              </select>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('cards')}
                className={`p-2 rounded ${viewMode === 'cards' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Sessions Display */}
        {filteredSessions.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Sessions Found</h3>
            <p className="text-gray-500 mb-6">
              {sessions.length === 0 
                ? "Start by creating your first session"
                : "Try adjusting your search or filters"
              }
            </p>
            {sessions.length === 0 && (
              <button
                onClick={handleCreateSession}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
                Create First Session
              </button>
            )}
          </div>
        ) : viewMode === 'cards' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSessions.map(session => {
              const attendanceStats = getSessionAttendanceStats(session.id);
              const context = userContexts?.find(ctx => ctx.id === session.contextId);
              
              return (
                <div key={session.id} className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                  {/* Card Header */}
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getSessionTypeIcon(session.sessionContext)}
                        <span className="text-sm text-gray-500">{context?.name}</span>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSessionCategoryColor(session.sessionCategory)}`}>
                        {session.sessionCategory}
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">
                      {session.sessionTitle}
                    </h3>
                    <p className="text-sm text-gray-600">{session.sessionType}</p>
                  </div>

                  {/* Card Content */}
                  <div className="p-4">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {new Date(session.sessionDate).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {session.sessionTime || 'No time set'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{session.subject}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {session.totalLearners} learners
                        </span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="flex items-center gap-1 mb-1">
                        <Target className="w-3 h-3 text-gray-400" />
                        <span className="text-xs text-gray-500">Grades:</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {session.grades.map(grade => (
                          <span key={grade} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                            {grade}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Attendance Stats */}
                    <div className="border-t border-gray-100 pt-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-600">Attendance Records</span>
                        <span className="text-xs text-gray-500">
                          {attendanceStats.recordCount} record{attendanceStats.recordCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {attendanceStats.recordCount > 0 ? (
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <div className={`text-sm font-medium ${
                              attendanceStats.averageAttendanceRate >= 90 ? 'text-green-600' :
                              attendanceStats.averageAttendanceRate >= 75 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {attendanceStats.averageAttendanceRate}% avg attendance
                            </div>
                            <div className="text-xs text-gray-500">
                              Last: {new Date(attendanceStats.lastRecorded).toLocaleDateString()}
                            </div>
                          </div>
                          <TrendingUp className={`w-4 h-4 ${
                            attendanceStats.averageAttendanceRate >= 90 ? 'text-green-500' :
                            attendanceStats.averageAttendanceRate >= 75 ? 'text-yellow-500' :
                            'text-red-500'
                          }`} />
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">No attendance recorded</div>
                      )}
                    </div>
                  </div>

                  {/* Card Actions */}
                  <div className="border-t border-gray-100 p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewSession(session)}
                          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditSession(session)}
                          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md transition-colors"
                          title="Edit Session"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSession(session.id, session.sessionTitle)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          title="Delete Session"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        onClick={() => handleRecordAttendance(session.id)}
                        className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                      >
                        <CheckCircle className="w-3 h-3" />
                        Record Attendance
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* List View */
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Session
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subject & Grades
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Learners
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Attendance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSessions.map(session => {
                    const attendanceStats = getSessionAttendanceStats(session.id);
                    const context = userContexts?.find(ctx => ctx.id === session.contextId);
                    
                    return (
                      <tr key={session.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              {getSessionTypeIcon(session.sessionContext)}
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                {session.sessionTitle}
                              </div>
                              <div className="text-sm text-gray-500">
                                {session.sessionType} • {context?.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {new Date(session.sessionDate).toLocaleDateString()}
                          </div>
                          <div className="text-sm text-gray-500">
                            {session.sessionTime || 'No time set'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{session.subject}</div>
                          <div className="text-sm text-gray-500">
                            {session.grades.join(', ')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {session.totalLearners}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {attendanceStats.recordCount > 0 ? (
                            <div>
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                attendanceStats.averageAttendanceRate >= 90 ? 'bg-green-100 text-green-800' :
                                attendanceStats.averageAttendanceRate >= 75 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {attendanceStats.averageAttendanceRate}%
                              </span>
                              <div className="text-xs text-gray-500 mt-1">
                                {attendanceStats.recordCount} record{attendanceStats.recordCount !== 1 ? 's' : ''}
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">No records</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewSession(session.id)}
                              className="text-blue-600 hover:text-blue-800"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleViewSession(session)}
                              className="text-blue-600 hover:text-blue-800"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditSession(session)}
                              className="text-gray-600 hover:text-gray-800"
                              title="Edit Session"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSession(session.id, session.sessionTitle)}
                              className="text-red-600 hover:text-red-800"
                              title="Delete Session"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionList;